var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { makeProvider } from '@/lti_providers';
import * as models from '@/models';
import { CoursePermissions as CPerm } from '@/permissions';
import { AssignmentsStore, CoursesStore, SiteSettingsStore, store, TenantsStore, UserStore, } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
// We can't make this is a `const enum` as we export it.
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var CourseState;
(function (CourseState) {
    CourseState["visible"] = "visible";
    CourseState["archived"] = "archived";
    CourseState["deleted"] = "deleted";
})(CourseState || (CourseState = {}));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function createCourseState(state) {
    switch (state) {
        case 'visible':
        case 'archived':
        case 'deleted':
            return CourseState[state];
        default:
            return utils.AssertionError.assertNever(state);
    }
}
const DEFAULT_COURSE_PERM_MAP = utils.mapToObject(Object.values(CPerm), value => [value.value, false]);
const canManageCoursePermissions = Object.freeze(Object.values(CPerm).filter(perm => perm.manageCourse));
const canManageAssignmentPermissions = Object.freeze(Object.values(CPerm).filter(perm => perm.manageAssignment));
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Course {
    id;
    name;
    createdAt;
    virtual;
    state;
    ltiProvider;
    assignmentIds;
    groupSets;
    snippets;
    tenantId;
    copyLockDate;
    price;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, name, createdAt, virtual, state, ltiProvider, assignmentIds, groupSets, snippets, tenantId, copyLockDate, price) {
        this.id = id;
        this.name = name;
        this.createdAt = createdAt;
        this.virtual = virtual;
        this.state = state;
        this.ltiProvider = ltiProvider;
        this.assignmentIds = assignmentIds;
        this.groupSets = groupSets;
        this.snippets = snippets;
        this.tenantId = tenantId;
        this.copyLockDate = copyLockDate;
        this.price = price;
        Object.freeze(this.assignmentIds);
        Object.freeze(this.groupSets);
        Object.freeze(this.snippets);
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isLTI() {
        return this.ltiProvider.isJust();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get assignments() {
        return utils.filterMap(this.assignmentIds, id => AssignmentsStore.getAssignment()(id));
    }
    get permissions() {
        return new Proxy(DEFAULT_COURSE_PERM_MAP, {
            get: (_, key) => this.hasPermission(CPerm[key]),
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            set() {
                throw new Error('Cannot change permissions!');
            },
        });
    }
    /**
     * Determines whether a user can view the members of (some) sections.
     *
     * @returns Whether the logged in user has the necessary permissions to view (some) section
     * members.
     */
    canViewSectionMembers() {
        if (!SiteSettingsStore.getSetting()('SECTIONS_IN_SUBMISSION_LIST_ENABLED', this.tenantId)) {
            return false;
        }
        const hasPermission = UserStore.hasCoursePermission();
        if (hasPermission(CPerm.can_view_course_sections, this) &&
            hasPermission(CPerm.can_list_course_users, this)) {
            return true;
        }
        if (hasPermission(CPerm.can_view_own_course_section_members, this)) {
            return true;
        }
        return false;
    }
    /** @returns If the current user can manage the course. */
    get canManage() {
        const perms = this.permissions;
        return canManageCoursePermissions.some(perm => perms[perm.value]);
    }
    /** @returns If the current user can manage the assignment. */
    get canManageAssignment() {
        const perms = this.permissions;
        return canManageAssignmentPermissions.some(perm => perm.manageAssignment && perms[perm.value]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canCreateAssignments() {
        return this.hasPermission(CPerm.can_create_assignment);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromApiServerData(data) {
        return new Course(data.id, data.name, data.createdAt, data.virtual, createCourseState(data.state), data.ltiProvider.map(prov => makeProvider(prov)), data.assignments.map(a => a.id), data.groupSets.map((groupSet) => ({
            assignment_ids: groupSet.assignmentIds,
            id: groupSet.id,
            maximum_size: groupSet.maximumSize,
            minimum_size: groupSet.minimumSize,
        })), data.snippets.map((snippet) => ({
            course: true,
            ...snippet,
        })), data.tenantId, data.copyLockDate, data.price.map(price => new models.CoursePrice(price)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isStudent() {
        return !(this.canManage || this.canManageAssignment);
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    get created_at() {
        return utils.formatDate(this.createdAt, true);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    hasPermission(permission) {
        return UserStore.hasCoursePermission()(permission, this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    update(props) {
        return new Course(this.id, props.name ?? this.name, this.createdAt, this.virtual, props.state ?? this.state, this.ltiProvider, props.assignmentIds ?? this.assignmentIds, props.groupSets ?? this.groupSets, props.snippets ?? this.snippets, this.tenantId, this.copyLockDate, props.price ?? this.price);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addAssignment(assignment) {
        return this.update({ assignmentIds: [...this.assignmentIds, assignment.id] });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isArchived() {
        return this.state === CourseState.archived;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get tenant() {
        return this.tenantId.chain(TenantsStore.getTenant());
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get showTenant() {
        const myTenant = store.getters['user/tenantId'];
        return (this.tenantId.isJust() &&
            (CoursesStore.getTenantCount() > 1 ||
                !myTenant.alt(this.tenantId).equals(this.tenantId)));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get canBeCopiedInto() {
        return (this.hasPermission(CPerm.can_edit_group_set) &&
            this.hasPermission(CPerm.can_manage_course_snippets) &&
            this.hasPermission(CPerm.can_create_assignment));
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], Course.prototype, "permissions", null);
